import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, TrendingUp, Heart, FolderOpen, Tag, Image as ImageIcon, ArrowLeft, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { processImageUrlSync } from "@/utils/imageProxy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatisticsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API}/statistics`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Statistics Dashboard
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Articles</p>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {stats?.total_articles || 0}
                </h3>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
                <ImageIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Favorites</p>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {stats?.total_favorites || 0}
                </h3>
              </div>
              <div className="bg-red-100 dark:bg-red-900 p-3 rounded-lg">
                <Heart className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Collections</p>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {stats?.total_collections || 0}
                </h3>
              </div>
              <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-lg">
                <FolderOpen className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Tags</p>
                <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {stats?.total_tags || 0}
                </h3>
              </div>
              <div className="bg-green-100 dark:bg-green-900 p-3 rounded-lg">
                <Tag className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Articles by Source */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Articles by Source
            </h2>
            <div className="space-y-4">
              {stats?.articles_by_source && Object.entries(stats.articles_by_source).map(([source, count]) => {
                const sourceColors = {
                  'misskon': 'bg-blue-500',
                  '4khd': 'bg-purple-500',
                  '06se': 'bg-green-500'
                };
                return (
                  <div key={source} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${sourceColors[source] || 'bg-gray-500'}`}></div>
                      <span className="text-gray-700 dark:text-gray-300 font-medium capitalize">{source}</span>
                    </div>
                    <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Tags */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Top Tags
            </h2>
            <div className="space-y-3">
              {stats?.top_tags && stats.top_tags.length > 0 ? (
                stats.top_tags.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-sm rounded-full">
                      {item.tag}
                    </span>
                    <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">{item.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400">No tags yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Top Articles */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Articles with Most Images
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats?.top_articles && stats.top_articles.length > 0 ? (
              stats.top_articles.map((article, index) => (
                <div
                  key={article.id}
                  className="group bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => navigate(`/article/${article.id}`)}
                >
                  <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-600 dark:to-gray-700 relative overflow-hidden flex items-center justify-center">
                    {article.thumbnail ? (
                      <img
                        src={processImageUrlSync(article.thumbnail, article.source)}
                        alt={article.title}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-semibold">
                      {article.image_count} images
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 capitalize">
                      {article.source}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="col-span-full text-gray-500 dark:text-gray-400">No articles with images yet</p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StatisticsPage;
